import type { EntrLocale } from '@/i18n';

const LOG_PREFIX = '[Service:EntrUrlState]';

// SPA is now served from root on a dedicated host
// (entr.dev.theartisan.ai / entr.theartisan.ai), not the legacy
// dev.theartisan.ai/entr-kiosk/ path. BASE_URL is '/' for both.
const DEFAULT_BASE_PATH = '/';
type LaunchField = 'apiKey' | 'kioskId' | 'stripeReaderId' | 'printer';
type QueryField = `${LaunchField}FromQuery`;
type LaunchState = Pick<RuntimeUrlState, LaunchField | QueryField>;

interface LaunchParam {
  field: LaunchField;
  query: readonly string[];
  storage: string;
}

// The first query name that is present wins, also with an empty value.
const LAUNCH_PARAMS: readonly LaunchParam[] = [
  {
    field: 'apiKey',
    query: ['api_key', 'key'],
    storage: 'entr_kiosk.launch.api_key',
  },
  {
    field: 'kioskId',
    query: ['kiosk_id'],
    storage: 'entr_kiosk.launch.kiosk_id',
  },
  {
    field: 'stripeReaderId',
    query: ['stripe_reader_id', 'reader_id'],
    storage: 'entr_kiosk.launch.stripe_reader_id',
  },
  {
    field: 'printer',
    query: ['printer', 'printer_id'],
    storage: 'entr_kiosk.launch.printer',
  },
];

const NO_LAUNCH_STATE: LaunchState = {
  apiKey: null,
  apiKeyFromQuery: null,
  kioskId: null,
  kioskIdFromQuery: null,
  stripeReaderId: null,
  stripeReaderIdFromQuery: null,
  printer: null,
  printerFromQuery: null,
};

let launchKioskSlug: string | null = null;

export interface RuntimeUrlState {
  slug: string | null;
  rawLocale: string | null;
  locale: EntrLocale | null;
  apiKey: string | null;
  apiKeyFromQuery: string | null;
  kioskId: string | null;
  kioskIdFromQuery: string | null;
  stripeReaderId: string | null;
  stripeReaderIdFromQuery: string | null;
  // Per-physical-kiosk printer name. Mirrors how stripe_reader_id is
  // passed: each kiosk's systemd unit appends `?printer=<cups_name>`
  // to its launch URL so the operator can re-assign printers without
  // a rebuild. DB-driven per-kiosk printer (kiosks.printer_details)
  // is the longer-term frontend/ parity story.
  printer: string | null;
  printerFromQuery: string | null;
  debug: boolean;
  hash: string;
}

function canUseWindow(): boolean {
  return typeof window !== 'undefined';
}

function log(message: string): void {
  if (import.meta.env.DEV) {
    console.info(message);
  }
}

function warn(message: string, error?: unknown): void {
  console.warn(message, error);
}

export function sanitizeKioskSlug(
  slug: string | null | undefined
): string | null {
  if (!slug) return null;
  const trimmed = slug.trim();
  if (!trimmed) return null;
  const cleaned = trimmed.split(/[?#&/]/)[0];
  return cleaned || null;
}

function resolveLocale(rawLocale: string | null): EntrLocale | null {
  if (!rawLocale) return null;
  const normalized = rawLocale
    .trim()
    .toLowerCase()
    .replace('_', '-')
    .split('-')[0];
  if (normalized === 'nl' || normalized === 'en') return normalized;
  return null;
}

function getBasePathSegments(): string[] {
  const baseUrl = getConfiguredBaseUrl();
  return baseUrl
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
}

function getConfiguredBaseUrl(): string {
  const baseUrl = import.meta.env.BASE_URL || '/';
  return baseUrl === '/' ? DEFAULT_BASE_PATH : baseUrl;
}

function getBasePath(): string {
  const baseUrl = getConfiguredBaseUrl();
  if (baseUrl === '/') return '/';
  return `${baseUrl.replace(/\/+$/, '')}/`;
}

function getRelativePathSegments(pathname: string): string[] {
  const pathSegments = pathname
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
  const baseSegments = getBasePathSegments();
  const matchesBasePath =
    baseSegments.length === 0 ||
    baseSegments.every((segment, index) => pathSegments[index] === segment);

  return matchesBasePath
    ? pathSegments.slice(baseSegments.length)
    : pathSegments;
}

function decodePathSegment(segment?: string): string | null {
  if (!segment) return null;

  try {
    return decodeURIComponent(segment);
  } catch (error) {
    warn(
      `${LOG_PREFIX} Failed to decode path segment "${segment}", using raw value`,
      error
    );
    return segment;
  }
}

function getStoredValue(key: string): string | null {
  if (!canUseWindow()) return null;

  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    warn(`${LOG_PREFIX} Failed to read "${key}" from localStorage`, error);
    return null;
  }
}

function setStoredValue(key: string, value: string): void {
  if (!canUseWindow()) return;

  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    warn(`${LOG_PREFIX} Failed to write "${key}" to localStorage`, error);
  }
}

function getPathState(
  pathname: string
): Pick<RuntimeUrlState, 'slug' | 'rawLocale' | 'locale'> {
  const [firstSegment, secondSegment] = getRelativePathSegments(pathname);
  const firstValue = decodePathSegment(firstSegment);
  const firstLocale = resolveLocale(firstValue);
  if (firstLocale) {
    return {
      slug: null,
      rawLocale: firstValue,
      locale: firstLocale,
    };
  }

  const slugSegment = firstSegment;
  const localeSegment = secondSegment;
  const rawSlug = decodePathSegment(slugSegment);
  const slug = sanitizeKioskSlug(rawSlug);
  if (rawSlug && slug !== rawSlug) {
    log(`${LOG_PREFIX} sanitized path slug "${rawSlug}" -> "${slug}"`);
  }
  const rawLocale = decodePathSegment(localeSegment);

  return {
    slug,
    rawLocale,
    locale: resolveLocale(rawLocale),
  };
}

function getConfiguredKioskSlug(): string | null {
  return import.meta.env.VITE_KIOSK_SLUG || null;
}

function queryField(field: LaunchField): QueryField {
  return `${field}FromQuery`;
}

function getLaunchState(
  params: URLSearchParams,
  slug: string | null
): LaunchState {
  const state = { ...NO_LAUNCH_STATE };
  for (const { field, query, storage } of LAUNCH_PARAMS) {
    const fromQuery = query.reduce<string | null>(
      (found, name) => found ?? params.get(name),
      null
    );
    state[queryField(field)] = fromQuery;
    state[field] = slug
      ? (fromQuery ?? getStoredValue(`${storage}.${slug}`))
      : fromQuery;
  }
  return state;
}

export function getRuntimeUrlState(): RuntimeUrlState {
  if (!canUseWindow()) {
    return {
      slug: null,
      rawLocale: null,
      locale: null,
      ...NO_LAUNCH_STATE,
      debug: false,
      hash: '',
    };
  }

  const currentUrl = new URL(window.location.href);
  const pathState = getPathState(currentUrl.pathname);
  const effectiveSlug =
    pathState.slug ?? getConfiguredKioskSlug() ?? launchKioskSlug;

  return {
    ...pathState,
    ...getLaunchState(currentUrl.searchParams, effectiveSlug),
    debug: currentUrl.searchParams.has('debug'),
    hash: currentUrl.hash,
  };
}

export function getPathKioskSlug(): string | null {
  return getRuntimeUrlState().slug;
}

export function getPathLocale(): EntrLocale | null {
  return getRuntimeUrlState().locale;
}

export function getRuntimeApiKey(): string | null {
  return getRuntimeUrlState().apiKey;
}

export function getRuntimeKioskId(): string | null {
  return getRuntimeUrlState().kioskId;
}

export function getRuntimeStripeReaderId(): string | null {
  return getRuntimeUrlState().stripeReaderId;
}

export function getRuntimePrinter(): string | null {
  return getRuntimeUrlState().printer;
}

export function isDebugUrlEnabled(): boolean {
  return getRuntimeUrlState().debug;
}

export function getKioskSlug(): string | null {
  return getPathKioskSlug() ?? getConfiguredKioskSlug() ?? launchKioskSlug;
}

export function persistLaunchCredentials(fallbackSlug?: string | null): void {
  const state = getRuntimeUrlState();
  const effectiveSlug = state.slug ?? fallbackSlug ?? null;
  if (!effectiveSlug) {
    if (state.apiKeyFromQuery || state.kioskIdFromQuery) {
      warn(
        `${LOG_PREFIX} persistLaunchCredentials(): missing slug, skipping persistence`
      );
    }
    return;
  }
  launchKioskSlug = effectiveSlug;

  for (const { field, storage } of LAUNCH_PARAMS) {
    const fromQuery = state[queryField(field)];
    if (fromQuery) setStoredValue(`${storage}.${effectiveSlug}`, fromQuery);
  }
}

export function resetRuntimeLaunchStateForTests(): void {
  launchKioskSlug = null;
}

export function buildKioskPath(locale?: EntrLocale | null): string {
  const basePath = getBasePath();
  return locale ? `${basePath}${locale}` : basePath;
}

export function buildCanonicalKioskUrl(
  locale: EntrLocale,
  debug: boolean
): string {
  const canonicalPath = buildKioskPath(locale);
  return debug ? `${canonicalPath}?debug` : canonicalPath;
}

function navigateToLocale(
  locale: EntrLocale,
  method: 'replaceState' | 'pushState',
  label: string
): void {
  if (!canUseWindow()) return;

  const state = getRuntimeUrlState();
  const canonicalUrl = buildCanonicalKioskUrl(locale, state.debug);
  const nextUrl = state.hash ? `${canonicalUrl}${state.hash}` : canonicalUrl;
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (currentUrl === nextUrl) return;

  window.history[method](window.history.state, '', nextUrl);
  log(`${LOG_PREFIX} ${label}(): ${currentUrl} -> ${nextUrl}`);
}

export function replaceWithCanonicalKioskUrl(locale: EntrLocale): void {
  navigateToLocale(locale, 'replaceState', 'replaceWithCanonicalKioskUrl');
}

export function pushKioskLocaleUrl(locale: EntrLocale): void {
  navigateToLocale(locale, 'pushState', 'pushKioskLocaleUrl');
}
